/**
 * R5 — /insegna page.
 *
 * Operatori autorizzati (titolare / super_admin) possono insegnare al
 * sistema regole commerciali in linguaggio naturale. Il sistema le
 * interpreta (via parser NL) e le salva per applicarle automaticamente
 * durante l'analisi delle nuove offerte.
 */
import { useState } from "react";
import {
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  Loader2,
  Lightbulb,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "../context/AuthContext";
import useLearningRules from "../hooks/useLearningRules";
import { formatErrorDetail } from "../lib/api";

const EXAMPLES = [
  "Se il cliente è del settore turismo, usa sempre acqua potabile come fluido",
  "Per il cliente Bluserena, la spedizione è sempre sud/isole",
  "Se il materiale è AISI 316, aggiungi la certificazione EN 10204-3.1",
  "Per famiglia F04 flange, la connessione predefinita è UNI 2277",
];

export default function InsegnaPage() {
  const { user } = useAuth();
  const canUse = hasRole(user, "titolare", "super_admin");
  const { items, loading, error, parseNL, create, remove } = useLearningRules();
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [savingRule, setSavingRule] = useState(false);

  if (!canUse) {
    return (
      <div className="max-w-2xl mx-auto p-8 text-[14px]"
           style={{ color: "var(--text-secondary)" }}>
        <div
          className="rounded-md px-4 py-3 flex items-start gap-2"
          style={{
            background: "#FEF3C7",
            border: "1px solid #FDE68A",
            color: "#92400E",
          }}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <div>
            <div className="font-semibold">Accesso riservato</div>
            <div className="text-[12px]">
              Solo titolare o super admin possono insegnare nuove regole.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const interpret = async () => {
    if (!text.trim()) return;
    setParsing(true);
    setParsed(null);
    try {
      const res = await parseNL(text);
      setParsed(res);
    } catch (e) {
      toast.error(formatErrorDetail(e));
    } finally {
      setParsing(false);
    }
  };

  const saveRule = async () => {
    if (!parsed?.valid) return;
    setSavingRule(true);
    try {
      await create({
        description: parsed.description,
        condition_field: parsed.condition.field,
        condition_op: parsed.condition.op,
        condition_value: parsed.condition.value,
        action_field: parsed.action.field,
        action_value: parsed.action.value,
        priority: parsed.priority || 10,
      });
      toast.success("Regola salvata — verrà applicata dalle prossime offerte");
      setText("");
      setParsed(null);
    } catch (e) {
      toast.error(formatErrorDetail(e));
    } finally {
      setSavingRule(false);
    }
  };

  const handleRemove = async (ruleId) => {
    if (!confirm("Eliminare questa regola?")) return;
    try {
      await remove(ruleId);
      toast.success("Regola eliminata");
    } catch (e) {
      toast.error(formatErrorDetail(e));
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="insegna-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles
            className="h-5 w-5"
            style={{ color: "var(--accent-primary)" }}
          />
          <h1
            className="text-[22px] font-semibold"
            style={{ color: "var(--text-primary)" }}
          >
            Insegna al sistema
          </h1>
        </div>
        <p className="text-[13px]"
           style={{ color: "var(--text-secondary)" }}>
          Scrivi una regola commerciale in italiano.
          Il sistema la interpreta e la salva per applicarla automaticamente
          ad ogni nuova offerta.
        </p>
      </div>

      {/* Composer */}
      <section
        className="rounded-md p-4 space-y-3 bg-white"
        style={{ border: "1px solid var(--border-subtle)" }}
        data-testid="insegna-composer"
      >
        <div className="text-[11px] uppercase tracking-wide font-semibold"
             style={{ color: "var(--text-muted)" }}>
          Nuova regola
        </div>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="es. «Se il cliente è del settore alimentare, usa EPDM come tenuta»"
          className="w-full text-[13px] p-3 rounded-md resize-none"
          style={{ border: "1px solid var(--border-subtle)" }}
          data-testid="rule-nl-input"
        />
        <div className="flex items-center justify-between">
          <div className="text-[11px] flex items-center gap-1"
               style={{ color: "var(--text-muted)" }}>
            <Lightbulb className="h-3 w-3" /> Esempi:
          </div>
          <button
            type="button"
            onClick={interpret}
            disabled={parsing || !text.trim()}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-medium text-white disabled:opacity-40"
            style={{ background: "var(--accent-primary)" }}
            data-testid="interpret-btn"
          >
            {parsing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Interpreta
          </button>
        </div>
        <ul className="flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => setText(ex)}
                className="px-2 py-1 rounded text-[11px]"
                style={{
                  background: "var(--bg-subtle)",
                  color: "var(--text-secondary)",
                  border: "1px solid var(--border-subtle)",
                }}
                data-testid={`example-${i}`}
              >
                {ex}
              </button>
            </li>
          ))}
        </ul>

        {/* Parsed preview */}
        {parsed && (
          <div
            className="rounded-md p-3 space-y-2"
            style={{
              background: parsed.valid ? "#F0FDF4" : "#FFFBEB",
              border: parsed.valid
                ? "1px solid #BBF7D0"
                : "1px solid #FDE68A",
            }}
            data-testid="parsed-preview"
          >
            <div className="flex items-center gap-2">
              {parsed.valid ? (
                <CheckCircle2 className="h-4 w-4" style={{ color: "#15803D" }} />
              ) : (
                <AlertTriangle className="h-4 w-4" style={{ color: "#B45309" }} />
              )}
              <div className="font-semibold text-[13px]"
                   style={{
                     color: parsed.valid ? "#166534" : "#92400E",
                   }}>
                {parsed.description || "Regola interpretata"}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[12px]">
              <div>
                <div className="text-[10px] uppercase tracking-wide"
                     style={{ color: "var(--text-muted)" }}>
                  Condizione
                </div>
                <div className="font-mono"
                     style={{ color: "var(--text-primary)" }}>
                  {parsed.condition?.field || "—"}{" "}
                  <span style={{ color: "var(--text-muted)" }}>
                    {parsed.condition?.op}
                  </span>{" "}
                  &quot;{parsed.condition?.value}&quot;
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide"
                     style={{ color: "var(--text-muted)" }}>
                  Azione
                </div>
                <div className="font-mono"
                     style={{ color: "var(--text-primary)" }}>
                  {parsed.action?.field || "—"} ← &quot;
                  {parsed.action?.value}&quot;
                </div>
              </div>
            </div>
            {parsed.warnings?.length > 0 && (
              <ul className="text-[11px] space-y-0.5"
                  style={{ color: "#92400E" }}>
                {parsed.warnings.map((w, i) => (
                  <li key={i}>⚠ {w}</li>
                ))}
              </ul>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setParsed(null)}
                className="h-7 px-3 rounded text-[11px]"
                style={{
                  border: "1px solid var(--border-subtle)",
                  color: "var(--text-secondary)",
                }}
                data-testid="parsed-discard-btn"
              >
                Scarta
              </button>
              <button
                type="button"
                onClick={saveRule}
                disabled={!parsed.valid || savingRule}
                className="inline-flex items-center gap-1 h-7 px-3 rounded text-[11px] font-medium text-white disabled:opacity-40"
                style={{ background: "var(--accent-primary)" }}
                data-testid="parsed-save-btn"
              >
                {savingRule ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3 w-3" />
                )}
                Salva regola
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Active rules list */}
      <section data-testid="rules-list-section">
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[11px] uppercase tracking-wide font-semibold"
               style={{ color: "var(--text-muted)" }}>
            Regole attive
          </div>
          <div className="text-[11px]"
               style={{ color: "var(--text-muted)" }}>
            {items.length} {items.length === 1 ? "regola" : "regole"}
          </div>
        </div>
        {error && (
          <div
            className="rounded-md px-3 py-2 text-[12px] mb-2"
            style={{
              background: "var(--semantic-red-bg)",
              color: "var(--semantic-red)",
            }}
          >
            {error}
          </div>
        )}
        {loading && (
          <div className="text-[12px] flex items-center gap-2"
               style={{ color: "var(--text-muted)" }}>
            <Loader2 className="h-3 w-3 animate-spin" />
            Caricamento…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div
            className="rounded-md p-6 text-center text-[12px]"
            style={{
              background: "var(--bg-subtle)",
              border: "1px dashed var(--border-subtle)",
              color: "var(--text-muted)",
            }}
          >
            Nessuna regola attiva. Insegnane una qui sopra.
          </div>
        )}
        {!loading && items.length > 0 && (
          <ul className="space-y-2" data-testid="rules-list">
            {items.map((r) => (
              <li
                key={r.id}
                className="rounded-md p-3 bg-white flex items-start justify-between gap-3"
                style={{ border: "1px solid var(--border-subtle)" }}
                data-testid={`rule-${r.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold"
                       style={{ color: "var(--text-primary)" }}>
                    {r.description}
                  </div>
                  <div className="text-[11px] font-mono mt-0.5"
                       style={{ color: "var(--text-muted)" }}>
                    SE {r.condition?.field} {r.condition?.op} &quot;
                    {r.condition?.value}&quot; → {r.action?.field} = &quot;
                    {r.action?.value}&quot;
                  </div>
                  <div className="text-[10px] mt-1 flex gap-3"
                       style={{ color: "var(--text-muted)" }}>
                    <span>priorità {r.priority ?? 0}</span>
                    <span>
                      applicata {r.times_applied ?? 0}{" "}
                      {r.times_applied === 1 ? "volta" : "volte"}
                    </span>
                    {r.created_by && <span>· {r.created_by}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(r.id)}
                  className="h-7 w-7 inline-flex items-center justify-center rounded transition-colors"
                  style={{ color: "var(--text-muted)" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#FEE2E2";
                    e.currentTarget.style.color = "#B91C1C";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "var(--text-muted)";
                  }}
                  data-testid={`delete-rule-${r.id}`}
                  title="Elimina regola"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
